import {
  Connection,
  Keypair,
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import * as multisig from '@sqds/multisig';
import { useWalletStore } from '@/stores/wallet';

export interface CreateMultisigParams {
  rpcUrl: string;
  programId: string;
  backendSignerPubkey: string;
}

export interface CreateMultisigResult {
  multisigPda: string;
  createKey: string;
  signature: string;
}

/**
 * Build a `multisigCreateV2` instruction with members `[user (all perms), backend (Vote only)]`,
 * threshold 2, sign with the user wallet AND the ephemeral createKey, submit. Returns the new
 * `multisigPda` and the create transaction signature.
 */
export async function createMultisig(
  params: CreateMultisigParams,
): Promise<CreateMultisigResult> {
  const wallet = useWalletStore.getState();
  const userPk = wallet.publicKey();
  if (!userPk) throw new Error('connect wallet first');

  const programId = new PublicKey(params.programId);
  const backendPk = new PublicKey(params.backendSignerPubkey);
  const conn = new Connection(params.rpcUrl, 'confirmed');

  const createKey = Keypair.generate();
  const [multisigPda] = multisig.getMultisigPda({ createKey: createKey.publicKey, programId });
  const [programConfigPda] = multisig.getProgramConfigPda({ programId });
  const programConfig = await multisig.accounts.ProgramConfig.fromAccountAddress(
    conn,
    programConfigPda,
  );

  const members = [
    {
      key: userPk,
      permissions: multisig.types.Permissions.all(),
    },
    {
      key: backendPk,
      permissions: multisig.types.Permissions.fromPermissions([multisig.types.Permission.Vote]),
    },
  ];

  const ix = multisig.instructions.multisigCreateV2({
    treasury: programConfig.treasury,
    creator: userPk,
    multisigPda,
    configAuthority: null,
    threshold: 2,
    members,
    timeLock: 0,
    createKey: createKey.publicKey,
    rentCollector: null,
    programId,
  });

  const blockhash = await conn.getLatestBlockhash('confirmed');
  const message = new TransactionMessage({
    payerKey: userPk,
    recentBlockhash: blockhash.blockhash,
    instructions: [ix],
  }).compileToV0Message();
  const tx = new VersionedTransaction(message);
  tx.sign([createKey]);
  const signedTx = await wallet.signTransaction(tx);
  const signature = await conn.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
  });
  await conn.confirmTransaction(
    { signature, blockhash: blockhash.blockhash, lastValidBlockHeight: blockhash.lastValidBlockHeight },
    'confirmed',
  );

  return {
    multisigPda: multisigPda.toBase58(),
    createKey: createKey.publicKey.toBase58(),
    signature,
  };
}
